/* eslint-disable no-case-declarations */
/* eslint-disable arrow-body-style */
import React, { useEffect, useRef, memo, useState, MutableRefObject, forwardRef, useImperativeHandle, lazy, Suspense } from "react";
import { Body1, InteractiveElement, styled, useWindowSize } from "@iventis/styles";
import { debounce } from "@mui/material";
import { InvocationEventType } from "@iventis/map-engine/src/types/events-invocation";
import { ValidationError } from "@iventis/map-engine/src/errors";
import { useDispatch, useSelector, useStore } from "react-redux";
import { convertReduxStoreReadToRXJS } from "@iventis/map-engine/src/utilities/redux-store-converter";
import { mapboxKey, useIsMobileUser, useKeyboardShortcut, useReadWritePermissionCheck, useRefEffectValue } from "@iventis/utilities";
import { EngineInterpreter } from "@iventis/map-engine/src/bridge/engine-generic";
import { Content } from "@iventis/translations/content/typed-content";
import { MappingEngine } from "@iventis/map-engine/src/types/store-schema";
import { getApiKey } from "@iventis/api-helpers/src/api-helpers";
import { useLocation } from "react-router";
import { useTreeBrowser } from "@iventis/tree-browser";
import { TreeBrowserDomainNodeContext } from "@iventis/tree-browser/implementations/library/helpers/library.helpers";
import { toast } from "@iventis/toasts/src/toast";
import { DataField } from "@iventis/domain-model/model/dataField";
import { ExportOptions } from "@iventis/map-engine/src/bridge/mapbox/engine-mapbox-types-and-constants";
import { CompositionUndoRedo } from "@iventis/map-engine/src/utilities/composition-undo-redo";
import { useQueryClient } from "@tanstack/react-query";
import { permissionsApi } from "@iventis/api/src/api";
import { translate } from "@iventis/translations/translation";
import { useHistory } from "@iventis/subscriptions";
import { COMMENT_ICON_DATA_URL, machineEventTypes } from "@iventis/map-engine";
import { IventisErrorBoundaryWithToast } from "@iventis/error-boundaries";
import { ExtractRefFromComponent } from "@iventis/types/useful.types";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { MobileCommentsButtons } from "@iventis/comments";
import { LoadingComponent } from "@iventis/components";
import { AnySupportedGeometry, MapObjectProperties } from "@iventis/map-types";
import { getMapObjectNameSystemDatafield } from "@iventis/datafields";
import { Feature } from "geojson";
import { UnrealEngineErrors } from "@iventis/map-engine/src/bridge/unreal/engine-unreal.types";
import { createDigitalTwinUrl } from "@iventis/digital-twin/src/digitial-twin-url-helpers";
import { DigitalTwinLoadingComponentProps, DigitalTwinNetworkComponentProps, DigitalTwinOccupiedComponentProps, getDigitalTwinStatus } from "@iventis/digital-twin";
import { DigitalTwinLicence } from "@iventis/domain-model/model/digitalTwinLicence";
import { UnrealEngine } from "@iventis/map-engine/src/bridge/unreal/engine-unreal";
import { DigitalTwinStatus } from "@iventis/digital-twin/src/digitial-twin-types";
import { useRefState } from "@iventis/utilities/src/hooks/use-ref-state";
import { RootDispatch, RootState } from "./state/root.store";
import { eventStream } from "./event-stream";
import { instantiateEngine, mapInterpreterEventHandler } from "./map-container.helpers";
import { map } from "./state/map.slice";
import { getDataFieldListItems } from "./services/data-field-list-item-service";
import { MapCommentsWithContext } from "./map-comments";
import { MapContainerUpdateEvent, mapContainerReducer, useMapContainerReducer } from "./map-container-reducer";
import { connectToDigitalTwinLicense, getMapCommentFeature } from "./state/map.slice.thunks";
import { getDigitalTwinLicence, getAllDigitalTwinLicences, getUserById } from "./map-container.apifunctions";
import { MapObjectMapTooltipComponent, MapObjectTooltipProps } from "./map-object-map-tooltip";
import { useMapUndoRedoContext } from "./map-undo-redo-context";
import { FROM_MAP_ROOT } from "./map-constants";
import { useMapScreenshot } from "./hooks/use-map-screenshot";

export type MapPopupParameters = Omit<MapObjectTooltipProps, "containerElementLeft" | "containerElementRight" | "containerElementTop" | "padding">;

type MapContainerProps = {
    gridColumn?: string;
    mappingEngine: MappingEngine;
    isSavedMapViewToolTipAllowed: MutableRefObject<boolean>;
    projectDatafields: DataField[];
    ref: MutableRefObject<MapContainerRef>;
    showDeleteModal: () => void;
    mapPermission: Authorisation;
    pageRef?: MutableRefObject<HTMLDivElement>;
    useLocalUnrealIp?: boolean;
};

export type MapContainerRef = {
    getMapPosition: () => ExportOptions;
    getCompositionUndoRedo: () => CompositionUndoRedo;
    getSnapshotDataUrl: () => Promise<string>;
};

const MapContainerRaw: React.FunctionComponent<MapContainerProps> = forwardRef<MapContainerRef, MapContainerProps>(
    ({ gridColumn, mappingEngine, isSavedMapViewToolTipAllowed, projectDatafields, showDeleteModal, pageRef, mapPermission, useLocalUnrealIp }, ref) => {
        const mapElement = useRef<HTMLDivElement>(null);
        const mapCommentsRef = useRef<ExtractRefFromComponent<typeof MapCommentsWithContext>>(null);
        const globalStore = useStore<RootState>();
        const dispatch: RootDispatch = useDispatch();
        const location = useLocation();
        const history = useHistory();
        const queryClient = useQueryClient();
        const isMobileUser = useIsMobileUser();
        const changeLevelAbortControllerRef = useRef<AbortController>();

        const unrealStatusInterval = useRef<NodeJS.Timeout>(null);
        const clearUnrealStatusInterval = () => {
            if (unrealStatusInterval.current) {
                clearInterval(unrealStatusInterval.current);
                unrealStatusInterval.current = null;
            }
        };

        const { digitalTwinconnectionError: digitalTwinConnectionError, currentDigitalTwinLicense, projectId, userId, userConnectedToDigitalTwin } = useSelector(
            (s: RootState) => ({
                digitalTwinconnectionError: s.mapReducer.mapModule.digitalTwinEngineData.connectionError,
                userConnectedToDigitalTwin: s.mapReducer.mapModule.digitalTwinEngineData.userConnectedToDigitalTwin,
                currentDigitalTwinLicense: s.mapReducer.mapModule.digitalTwinEngineData.currentDigitalTwinLicence,
                projectId: s.auth.user.projectId,
                userId: s.auth.user.userId,
            })
        );

        const [
            {
                popupParameters,
                mapElementSize: { mapElementHeight, mapElementWidth },
            },
            sendMapContainerEvent,
        ] = useMapContainerReducer(mapContainerReducer, {
            [MapContainerUpdateEvent.SHOW_DELETE_OBJECT_MODAL]: showDeleteModal,
        });

        const { commentsDrawingMode, selectedComment } = useSelector((s: RootState) => ({
            commentsDrawingMode: s.mapReducer.mapModule.commentsDrawingMode,
            selectedComment: s.mapReducer.mapModule.commentsSelected.value?.[0],
        }));
        const commentIsMoving = commentsDrawingMode === "panning" || commentsDrawingMode === "awaitingConfirmation" || commentsDrawingMode === "dragging";
        // Click away listener for the view all comments list
        useEffect(() => {
            const clickListener = (event) => {
                if (mapCommentsRef.current != null && mapCommentsRef.current.commentsContainerRef.current != null) {
                    // If the click is inside the comments container, or on the map itself, do nothing
                    if (
                        mapCommentsRef.current.commentsContainerRef.current.contains(event.target as Node) ||
                        (event.target as HTMLCanvasElement)?.className === "mapboxgl-canvas"
                    ) {
                        return;
                    }
                    mapCommentsRef.current?.closeViewAllCommentsList();
                }
            };
            pageRef.current?.addEventListener("click", clickListener);
            return () => {
                pageRef.current?.removeEventListener("click", clickListener);
            };
        }, [mapCommentsRef, pageRef]);

        const commentRef = useRef<HTMLButtonElement>(null);

        const [mapInterpreter, mapInterpreterRef, setMapInterpreter] = useRefState<EngineInterpreter>();

        const [digitalTwinStatus, setDigitalTwinStatus] = useState<DigitalTwinStatus>();

        const [{ isLoadingTree }, treeBrowserContext] = useTreeBrowser(TreeBrowserDomainNodeContext, null, "isLoadingTree");

        const undoRedo = useMapUndoRedoContext();

        const undoRedoRef = useRefEffectValue(undoRedo);

        const { screenHeight, screenWidth } = useWindowSize();

        const showMapBackgrondErrorToast = (mapBackgroundName: string) => {
            toast.error(
                {
                    title: translate(Content.map11.mapBackgroundError.title),
                    message: (
                        <Body1>
                            {translate(Content.map11.mapBackgroundError.part1)}
                            <MapBackgroundNameText>{` ${mapBackgroundName} `}</MapBackgroundNameText>
                            {translate(Content.map11.mapBackgroundError.part2)}
                            <MapBackgroundNameText>{` ${mapBackgroundName} `}</MapBackgroundNameText>
                            {translate(Content.map11.mapBackgroundError.part3)}
                        </Body1>
                    ),
                    testId: "map-background-error",
                },
                {
                    key: "map-background-error",
                    persist: true,
                }
            );
        };

        const triggerAndCreateAbortControllerForLevelChange = () => {
            if (changeLevelAbortControllerRef.current != null) {
                changeLevelAbortControllerRef.current.abort();
            }
            changeLevelAbortControllerRef.current = new AbortController();
            return changeLevelAbortControllerRef.current;
        };

        useEffect(() => {
            sendMapContainerEvent({
                type: MapContainerUpdateEvent.SET_MAP_ELEMENT_SIZE,
                payload: { mapElementHeight: mapElement.current?.clientHeight, mapElementWidth: mapElement.current?.clientWidth },
            });
        }, [mapElement.current?.clientHeight, mapElement.current?.clientWidth, screenHeight, screenWidth, gridColumn]);

        useEffect(() => {
            if (mapInterpreter) {
                mapInterpreter.onResize();
            }
        }, [gridColumn]);

        const [engineRestarting, setEngineRestarting] = useState(false);

        const [engineNetworkError, setEngineNetworkError] = useState(false);

        const checkUnrealStatus = async () => {
            if (currentDigitalTwinLicense == null) {
                setDigitalTwinStatus("occupied");
                clearUnrealStatusInterval();
                return;
            }

            // Get the latest licence status
            const licence: DigitalTwinLicence = await getDigitalTwinLicence(projectId, currentDigitalTwinLicense.id);
            const updatedDtStatus = getDigitalTwinStatus(licence, userId);
            setDigitalTwinStatus(updatedDtStatus);
            dispatch(map.actions.updateDigitalTwinLicence({ licence, error: digitalTwinConnectionError }));
            switch (updatedDtStatus) {
                case "error":
                    // If the license has errored, we need to try connect again and get a different license
                    dispatch(connectToDigitalTwinLicense());
                    break;
                case "occupied":
                    // If it is occupied, we stop checking status until the user clicks retry
                    clearUnrealStatusInterval();
                    break;
                case "available":
                    // Once the instance is ready we can initialise the engine
                    if (mappingEngine === MappingEngine.Unreal) {
                        (mapInterpreterRef.current as UnrealEngine).initialise(licence);
                    }
                    clearUnrealStatusInterval();
                    break;
                default:
                    break;
            }
        };

        // Initialise map after render
        useEffect(() => {
            clearUnrealStatusInterval();
            setEngineNetworkError(false);

            if (mapInterpreter) {
                mapInterpreter.destroy();
                // Remove any leftover mapbox query string parameters
                const searchParams = new URLSearchParams(location.search);
                history.replace({
                    search: searchParams.toString(),
                    hash: "",
                });
            }

            // If a map has already been opened and closed since refreshing, a new subject needs to be created in eventStream
            if (eventStream.subject.isStopped) eventStream.restart();

            const { store, complete } = convertReduxStoreReadToRXJS<RootState>(globalStore, (state) => state.mapReducer.mapModule);

            const localMapInterpreter = instantiateEngine(
                mappingEngine,
                store,
                globalStore,
                eventStream,
                mapElement.current,
                mapboxKey,
                async (_digitalTwinLicence: DigitalTwinLicence) => {
                    if (useLocalUnrealIp) {
                        return "http://localhost";
                    }
                    return createDigitalTwinUrl(_digitalTwinLicence);
                },
                () => {
                    return getApiKey(permissionsApi, userId);
                },
                getDataFieldListItems(queryClient, true),
                isMobileUser,
                (isConnecting: boolean) => {
                    setEngineRestarting(isConnecting);
                },
                (error: UnrealEngineErrors) => {
                    // switch to mapbox engine, occurs when something went wrong with unreal engine
                    switch (error) {
                        case UnrealEngineErrors.CONNECTION_FAILED:
                            setEngineNetworkError(true);
                            break;
                        case UnrealEngineErrors.ENGINE_ERROR:
                            dispatch(map.actions.setMappingEngine(MappingEngine.Mapbox));
                            toast.error({
                                title: translate(Content.common.error),
                                message: translate(Content.errors.digital_twin.generic),
                            });
                            break;
                        case UnrealEngineErrors.OCCUPIED:
                            dispatch(map.actions.setMappingEngine(MappingEngine.Mapbox));
                            toast.warning({
                                title: translate(Content.common.warning),
                                message: translate(Content.errors.digital_twin.occupied),
                            });
                            break;
                        case UnrealEngineErrors.AFK_TIMEOUT:
                            dispatch(map.actions.setMappingEngine(MappingEngine.Mapbox));
                            toast.warning({
                                title: translate(Content.common.warning),
                                message: translate(Content.errors.digital_twin.afk),
                            });
                            break;
                        default:
                            dispatch(map.actions.setMappingEngine(MappingEngine.Mapbox));
                            toast.error({
                                title: translate(Content.common.error),
                                message: translate(Content.errors.digital_twin.generic),
                            });
                            break;
                    }
                },
                setWebGLSupported
            );

            // Start interval if the map is unreal
            if (mappingEngine === MappingEngine.Unreal) {
                if (useLocalUnrealIp) {
                    setDigitalTwinStatus("available");
                    localMapInterpreter.initialise(null);
                    clearUnrealStatusInterval();
                } else {
                    // Keep polling instance status every 10 seconds
                    unrealStatusInterval.current = setInterval(checkUnrealStatus, 10000);
                    checkUnrealStatus();
                }
            } else {
                setDigitalTwinStatus(null);
                // If the user switches mapping engine whilst the unreal engine is loading we want to clear the status check interval
                clearUnrealStatusInterval();
            }

            // Change the state of the store
            const storeEventSub = localMapInterpreter.storeEvents.subscribe(
                mapInterpreterEventHandler(
                    globalStore,
                    dispatch,
                    undoRedoRef,
                    treeBrowserContext,
                    debounce(() => dispatch(map.actions.setSavedMapViewTooltip(isSavedMapViewToolTipAllowed.current)), 2000),
                    history,
                    isMobileUser,
                    sendMapContainerEvent,
                    triggerAndCreateAbortControllerForLevelChange,
                    showMapBackgrondErrorToast
                )
            );
            setMapInterpreter(localMapInterpreter);
            return () => {
                // Clearup any state stuff (comments, mapobject tooltips) - this must be done before destroying the interpreter as it holds needed data
                sendMapContainerEvent({ type: MapContainerUpdateEvent.HIDE_POPUP });
                eventStream.next({ type: machineEventTypes.selectComment, payload: null, from: FROM_MAP_ROOT });

                complete();
                localMapInterpreter.destroy();
                storeEventSub.unsubscribe();
                clearUnrealStatusInterval();
            };
        }, [mappingEngine, currentDigitalTwinLicense?.id]);

        useEffect(() => {
            if (mapInterpreter === undefined) {
                return;
            }

            // Events based on user interaction.
            const invocationEventSub = mapInterpreter.invocationEvents.subscribe((event) => {
                switch (event.type) {
                    case InvocationEventType.MOUSE_MOVE: {
                        const feature = event.body?.features[0];

                        // Only show the popup if we have one of our own features, which has a layer name and an object name
                        if (feature) {
                            const layerId = feature.properties?.layerid;
                            const mapObjectNameDataField = getMapObjectNameSystemDatafield(projectDatafields);
                            const featureName = feature.properties[mapObjectNameDataField.id];
                            if (layerId && typeof featureName === "string") {
                                const layer = globalStore.getState().mapReducer.mapModule.layers?.value.find((l) => l.id === layerId);
                                if (layer) {
                                    sendMapContainerEvent({
                                        type: MapContainerUpdateEvent.SET_POPUP_PARAMETERS,
                                        payload: {
                                            layerId,
                                            mapObject: event.body.features[0] as Feature<AnySupportedGeometry, MapObjectProperties>,
                                            cursorX: event.body.x,
                                            cursorY: event.body.y,
                                        },
                                    });
                                    return;
                                }
                            }
                        }
                        sendMapContainerEvent({ type: MapContainerUpdateEvent.HIDE_POPUP });
                        break;
                    }
                    case InvocationEventType.MOUSE_OUT: {
                        sendMapContainerEvent({ type: MapContainerUpdateEvent.HIDE_POPUP });
                        break;
                    }
                    case InvocationEventType.PAN_START: {
                        sendMapContainerEvent({ type: MapContainerUpdateEvent.HIDE_POPUP });
                        break;
                    }
                    case InvocationEventType.VALIDATION_ERROR: {
                        let message: string;
                        let testId: string;
                        switch (event.body.message) {
                            case ValidationError.POLYGON_FEW_NODES: {
                                message = Content.errors.polygon_edit_error;
                                testId = "poylgon-few-nodes-error-toast";
                                break;
                            }
                            case ValidationError.LINESTRING_FEW_NODES: {
                                message = Content.errors.linestring_edit_error;
                                testId = "line-few-nodes-error-toast";
                                break;
                            }
                            default:
                                message = Content.errors.unknown_edit_error;
                                break;
                        }

                        toast.warning({
                            title: translate(Content.common.warning),
                            testId,
                            message: translate(message),
                            overridingToastIcon: event.body.icon ? event.body.icon : "triangle-exclamation",
                        });
                        break;
                    }
                    case InvocationEventType.MAP_LOADED:
                        window.dispatchEvent(new Event(InvocationEventType.MAP_LOADED));
                        break;
                    case InvocationEventType.MAP_ERRORED:
                        window.dispatchEvent(new CustomEvent(InvocationEventType.MAP_ERRORED, { detail: event.body }));
                        break;
                    case InvocationEventType.DRAW_OUTSIDE_CAD:
                        toast.warning({
                            title: translate(Content.common.warning),
                            message: translate(Content.map9.sitemapVersion.drawOutsideCADPerimeter),
                            testId: "draw-outside-cad-error-toast",
                        });
                        break;
                    case InvocationEventType.NEWLY_DRAWN_OBJECT_FILTERED_BY_DATE_TIME_FILTER:
                        toast.info({
                            title: translate(Content.map10.dateTimeFilter.newObjectFilteredTitle),
                            message: translate(Content.map10.dateTimeFilter.newObjectFilteredBody),
                            testId: "map-object-filtered-by-date-time-filter",
                        });
                        break;
                    default:
                        throw new Error("Event not implemented");
                }
            });

            // eslint-disable-next-line consistent-return
            return () => {
                invocationEventSub.unsubscribe();
            };
        }, [mapInterpreter]);

        // Function to be used in a callback to set whether WebGL is supported
        // This is needed as the check must be done before map store events start being processed
        const setWebGLSupported = (supported: boolean) => {
            dispatch(map.actions.setWebGLSupported(supported));
        };

        useImperativeHandle(
            ref,
            () => ({
                getMapPosition: () => mapInterpreter?.getExportPosition(),
                getCompositionUndoRedo: () => mapInterpreter?.getCompositionUndoRedo(),
                getSnapshotDataUrl: () => mapInterpreter?.getSnapshotDataUrl(),
            }),
            [mapInterpreter]
        );

        // If we're not in comments drawing mode, we don't want to show the view all comments list
        useEffect(() => {
            if (commentsDrawingMode == null) {
                mapCommentsRef.current?.closeViewAllCommentsList();
            }
        }, [mapCommentsRef.current, commentsDrawingMode == null]);

        const showCommentIconInMiddleOfScreen = isMobileUser && (commentIsMoving || commentsDrawingMode === "composition");

        // Lazy load the digital twin loading components as the SVGs are quite large
        const [DigitalTwinLoadingComponent, setDigitalTwinLoadingComponent] = useState<React.LazyExoticComponent<React.FunctionComponent<DigitalTwinLoadingComponentProps>>>();
        const [DigitalTwinOccupiedComponent, setDigitalTwinOccupiedComponent] = useState<React.LazyExoticComponent<React.FunctionComponent<DigitalTwinOccupiedComponentProps>>>();
        const [DigitalTwinRestatingComponent, setDigitalTwinRestatingComponent] = useState<React.LazyExoticComponent<React.FunctionComponent<{}>>>();
        const [DigitalTwinNetworkComponent, setDigitalTwinNetworkComponent] = useState<React.LazyExoticComponent<React.FunctionComponent<DigitalTwinNetworkComponentProps>>>();

        useEffect(() => {
            if (digitalTwinStatus !== null) {
                const TwinLoading = lazy(() => import("@iventis/digital-twin").then((module) => ({ default: module.DigitalTwinLoadingComponent })));
                const TwinOccupied = lazy(() => import("@iventis/digital-twin").then((module) => ({ default: module.DigitalTwinOccupiedComponent })));
                const TwinRestarting = lazy(() => import("@iventis/digital-twin").then((module) => ({ default: module.DigitalTwinRestartingComponent })));
                const TwinNetwork = lazy(() => import("@iventis/digital-twin").then((module) => ({ default: module.DigitalTwinNetworkComponent })));
                setDigitalTwinLoadingComponent(TwinLoading);
                setDigitalTwinOccupiedComponent(TwinOccupied);
                setDigitalTwinRestatingComponent(TwinRestarting);
                setDigitalTwinNetworkComponent(TwinNetwork);
            }
        }, [digitalTwinStatus]);

        const [hasReadPermissionOnMap] = useReadWritePermissionCheck(mapPermission);

        const { takeScreenshot } = useMapScreenshot({ allowScreenshot: () => !isLoadingTree && hasReadPermissionOnMap });
        useKeyboardShortcut("s", { ctrl: true, shift: true }, takeScreenshot);

        const engineLoading =
            digitalTwinStatus == null ||
            digitalTwinStatus === "starting" ||
            digitalTwinStatus === "searching" ||
            digitalTwinStatus === "error" ||
            (digitalTwinStatus === "available" && !userConnectedToDigitalTwin);

        const engineErrored = engineRestarting || engineNetworkError;

        return (
            <>
                {mappingEngine === MappingEngine.Unreal && (
                    <>
                        {engineNetworkError && (
                            <Suspense fallback={<LoadingComponent />}>
                                {DigitalTwinNetworkComponent ? (
                                    <DigitalTwinNetworkComponent
                                        url={new URL(createDigitalTwinUrl(currentDigitalTwinLicense)).hostname}
                                        onReturnToMap={() => {
                                            dispatch(map.actions.setMappingEngine(MappingEngine.Mapbox));
                                        }}
                                    />
                                ) : (
                                    <LoadingComponent />
                                )}
                            </Suspense>
                        )}
                        {engineRestarting && (
                            <Suspense fallback={<LoadingComponent />}>{DigitalTwinRestatingComponent ? <DigitalTwinRestatingComponent /> : <LoadingComponent />}</Suspense>
                        )}
                        {engineLoading && !engineErrored && (
                            <Suspense fallback={<LoadingComponent />}>
                                {DigitalTwinLoadingComponent ? <DigitalTwinLoadingComponent status={digitalTwinStatus} /> : <LoadingComponent />}
                            </Suspense>
                        )}
                        {digitalTwinStatus === "occupied" && (
                            <Suspense fallback={<LoadingComponent />}>
                                {DigitalTwinOccupiedComponent ? (
                                    <DigitalTwinOccupiedComponent
                                        error={digitalTwinConnectionError}
                                        getLicences={() => {
                                            return getAllDigitalTwinLicences(projectId);
                                        }}
                                        getUser={(id) => {
                                            return getUserById(id);
                                        }}
                                        onRetryClick={async () => {
                                            dispatch(connectToDigitalTwinLicense());
                                        }}
                                    />
                                ) : (
                                    <LoadingComponent />
                                )}
                            </Suspense>
                        )}
                    </>
                )}
                <StyledContainer data-testid="map-container" ref={mapElement} />
                {popupParameters && (
                    <MapObjectMapTooltipComponent
                        layerId={popupParameters.layerId}
                        mapObject={popupParameters.mapObject}
                        cursorX={popupParameters.cursorX}
                        cursorY={popupParameters.cursorY}
                        containerElementLeft={mapElement?.current?.offsetLeft || 0}
                        containerElementRight={mapElement?.current?.offsetLeft + mapElement?.current?.clientWidth || 0}
                        containerElementTop={mapElement?.current?.offsetTop || 0}
                    />
                )}
                {showCommentIconInMiddleOfScreen && mappingEngine !== MappingEngine.Unreal && (
                    <StyledCommentIcon
                        ref={commentRef}
                        mapElementHeight={mapElementHeight}
                        mapElementWidth={mapElementWidth}
                        onClick={() => eventStream.next({ type: machineEventTypes.addCommentInMobile })}
                        data-testid="add-comment-mobile-button"
                    />
                )}
                <IventisErrorBoundaryWithToast toast={toast}>
                    <MapCommentsWithContext
                        ref={mapCommentsRef}
                        commentIsMoving={commentIsMoving}
                        mapPermission={mapPermission}
                        mapElementHeight={mapElementHeight}
                        mapElementWidth={mapElementWidth}
                    />
                </IventisErrorBoundaryWithToast>
                {/* Change false to only show while dragging */}
                {isMobileUser && commentsDrawingMode === "awaitingConfirmation" && (
                    <MobileCommentsButtons
                        translate={translate}
                        handleCancelClick={() => {
                            // Cancel the move
                            eventStream.next({ type: machineEventTypes.cancelCommentMove });
                            // Focus on the comment at the previous location
                            dispatch(getMapCommentFeature({ commentId: selectedComment?.commentId, focus: true, panTo: true }));
                        }}
                        handleMoveClick={() => eventStream.next({ type: machineEventTypes.confirmCommentMove })}
                    />
                )}
            </>
        );
    }
);

const StyledCommentIcon = styled(InteractiveElement)<{ mapElementHeight: number; mapElementWidth: number }>`
    position: absolute;
    top: ${({ mapElementHeight }) => (mapElementHeight ? mapElementHeight / 2 - 22 : 0)}px;
    left: ${({ mapElementWidth }) => (mapElementWidth ? mapElementWidth / 2 - 22 : 0)}px;
    z-index: 100;
    width: 44px;
    height: 44px;
    background-image: url(${COMMENT_ICON_DATA_URL});
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
`;

const StyledContainer = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
`;

const MapBackgroundNameText = styled.span`
    font-weight: 700;
`;

export const MapContainer = memo(MapContainerRaw, (props, nextProps) => props.gridColumn === nextProps.gridColumn && props.mappingEngine === nextProps.mappingEngine);
